<script setup lang="ts">
import type { BlockHighlightsFragment, StaffMemberCategoryFragment, StaffMemberThumbFragment } from '#graphql-operations'

interface HighlightsStaffProps {
  data: BlockHighlightsFragment
}

const props = defineProps<HighlightsStaffProps>()

const staffCarouselId = useId()
const { data: staffItems } = await useAsyncData(`blockHighlightsStaff-${props.data.id}`, async () => {
  if (props.data.staffMembersCategory.length) {
    const res = await useGraphqlQuery('Entries', {
      section: ['staffMember'],
      relatedTo: (props.data.staffMembersCategory as StaffMemberCategoryFragment[]).map(item => item!.id),
    })

    return (res.data.entries?.filter?.(Boolean) || []) as StaffMemberThumbFragment[]
  }
  return Promise.resolve(props.data.staffMembers.filter(Boolean) as StaffMemberThumbFragment[])
})
</script>

<template>
  <Section v-if="(staffItems?.length || 0) > 0" padded class="blockHighlights blockHighlightsStaff">
    <div v-if="data.heading || data.subheading" class="container">
      <Text v-if="data.heading" class="typo-serif-4 mb-7.5 text-center lg:mb-20" :html="data.heading" />
      <Text v-if="data.subheading" :html="data.subheading" class="typo-serif-2.5 mb-7.5" />
    </div>
    <div class="desktop-only">
      <ul class="container layout-grid">
        <li v-for="staff in staffItems" :key="staff.id" class="col-span-3">
          <CardStaff :data="staff" />
        </li>
      </ul>
    </div>
    <div class="mobile-only">
      <BlossomCarousel :id="staffCarouselId" as="ul" class="carousel size-full">
        <li
          v-for="staff in staffItems"
          :key="staff.id"
          data-blossom-slide
          class="slide flex-shrink-0"
        >
          <CardStaff :data="staff" />
        </li>
      </BlossomCarousel>
      <div class="container c-$accent-color mt-5 flex gap-4 items-center justify-between lg:mt-12.5">
        <BlossomPrev :for="staffCarouselId" :aria-label="$t('previous')">
          <ButtonIcon as="span" icon="chevron-left" />
        </BlossomPrev>
        <BlossomDots :for="staffCarouselId" class="flex gap-2 justify-center" />
        <BlossomNext :for="staffCarouselId" :aria-label="$t('next')">
          <ButtonIcon as="span" icon="chevron-right" />
        </BlossomNext>
      </div>
    </div>
    <ul class="container c-$accent-color mt-5 flex gap-2 w-full items-center justify-center lg:c-$primary-color lg:mt-12.5">
      <li v-for="link in data.links" :key="link!.id">
        <ButtonLink :link="link?.customLink" />
      </li>
    </ul>
  </Section>
</template>

<style scoped>
.blockHighlightsStaff .carousel {
  overflow: auto hidden;
  scrollbar-width: none;
  scroll-snap-type: x mandatory;

  @screen lt-lg {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: 100%;
    column-gap: calc(var(--spacing) * 4);
    padding-inline: calc(var(--spacing) * 4);
    scroll-padding-inline: calc(var(--spacing) * 4);
  }
}

.blockHighlightsStaff :deep([data-blossom-slide]) {
  scroll-snap-align: start;
}

.blockHighlightsStaff :deep(.blossom-dot) {
  background-color: var(--blossom-dot-color, currentColor);
  border-radius: 100%;

  inline-size: calc(var(--spacing) * 1.8);
  block-size: calc(var(--spacing) * 2);

  opacity: var(--blossom-dot-opacity, 0.35);

  &[aria-current='true'] {
    opacity: 1;
  }
}
</style>
